import { moneyFromMajorFloat, sumMoney, zeroMoney } from "@performyard/money";

import type { DecisionRow, MyDecisionsDirectReportRow } from "../../types";
import type { CompensationGroupUser, GroupBudgetFigures, IncreaseValues } from "./types";

import { SUPER_ROW_ID_PREFIX, toDecisionRow } from "../CellRenderers";
import {
	isOverBudgetMoney,
	percentAllocated,
	remainingBudget,
	resolveGroupBudget,
	sumAmounts,
} from "../SharedComponents/money-aggregation";

export const mapGroupUsersToDecisionRows = (
	groupUsers: MyDecisionsDirectReportRow[],
	userID: string | undefined
): DecisionRow[] => groupUsers.map((dr, index) => toDecisionRow(dr, index, userID));

export const buildGroupRowDataWithExpansion = (
	baseRows: DecisionRow[],
	expandedRowId: string | null,
	notesPayloadKeyFn: (row: DecisionRow) => string
): DecisionRow[] => {
	if (expandedRowId == null) {
		return baseRows;
	}
	return baseRows.flatMap((row) => {
		if (row._id !== expandedRowId) {
			return [row];
		}
		return [
			row,
			{
				...row,
				_id: `${SUPER_ROW_ID_PREFIX}${row._id}`,
				superRow: true,
				parentRowId: notesPayloadKeyFn(row),
			},
		];
	});
};

export const computeGroupBudgetFigures = (
	groupUsers: CompensationGroupUser[],
	baseGroupRowData: DecisionRow[],
	increaseValues: IncreaseValues,
	recommendedIncreasePercent: number
): GroupBudgetFigures => {
	// The group's budget was allocated and snapshotted at creation and rides on
	// every row; the percent derivation is the fallback for older cycles.
	const groupCurrentSalary = sumAmounts(groupUsers.map((u) => u.currentSalary));
	const budget = resolveGroupBudget(
		groupUsers[0]?.groupIncreaseBudget,
		groupCurrentSalary,
		recommendedIncreasePercent
	);

	const allocated = sumMoney(
		baseGroupRowData.map((row) => {
			// A draft is a user-typed number and becomes exact money here.
			//
			// The `|| 0` this replaces coerced a *missing* draft to the number 0, which
			// then satisfied the `typeof === "number"` test — so a row with no draft
			// contributed zero and the `row.proposed_increase_amount` fallback below was
			// unreachable. That is a real bug (a saved amount does not count toward
			// "allocated" until it is re-edited), and the test at
			// `MyDecisionsGroups.unit.test.tsx:231` documents the intended sum in a comment
			// while asserting the buggy one.
			//
			// It is left as-is deliberately: this ticket is about money precision, and
			// changing which rows count toward a manager's allocated budget is a product
			// decision, not a rounding fix. Tracked separately.
			const draftAmount = increaseValues[row._id]?.amount || 0;
			if (typeof draftAmount === "number") return moneyFromMajorFloat(draftAmount);
			return row.proposed_increase_amount ?? zeroMoney();
		})
	);

	return {
		groupIncreaseBudget: budget,
		groupAllocated: allocated,
		// This view shows a whole percent, unlike the two-decimal figure on the
		// budget-reviews page. Kept as-is rather than silently changed.
		groupPercentAllocated: Math.round(percentAllocated(allocated, budget)),
		groupRemaining: remainingBudget(budget, allocated),
		isGroupOverBudget: isOverBudgetMoney(allocated, budget),
	};
};
